import { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Header } from "./Header";
import { Sidebar, NavItem } from "./Sidebar";
import { Footer } from "./Footer";
import { PageTransition } from "./PageTransition";
import { Breadcrumb } from "../ui/Breadcrumb";
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { RoleSessionExpiredModal } from "../auth/RoleSessionExpiredModal";
import {
  HomeIcon,
  RocketLaunchIcon,
  CpuChipIcon,
  KeyIcon,
  CubeIcon,
  ShoppingBagIcon,
  UsersIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  RectangleStackIcon,
  CircleStackIcon,
  DocumentIcon,
  LockClosedIcon,
  PlayCircleIcon,
  ClockIcon,
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ChartBarIcon,
  LifebuoyIcon,
  QueueListIcon,
  FolderIcon,
  ServerStackIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import logoImage from "../../assets/logo.png";
import { UnifiedInboxDropdown } from "../../features/inbox";
import type { AvailableRole, ActiveRoleDetails } from "../../types/auth.types";
import type { ExpiredRoleInfo } from "../../contexts/AuthContext";

export interface DashboardLayoutProps {
  children: ReactNode;
  tenantCode?: string;
  tenantName?: string;
  tenantLogoUrl?: string;
  userName?: string;
  userEmail?: string;
  /** Currently active role session (if any) */
  activeRole?: ActiveRoleDetails | null;
  /** Whether a role session is currently active */
  isRoleSessionActive?: boolean;
  /** Available roles that the user can activate */
  availableRoles?: AvailableRole[];
  /** Callback to activate a role */
  onActivateRole?: (tenantRoleId: string, durationHours?: number) => Promise<void>;
  /** Default role session duration from tenant settings (hours, 1-8) */
  defaultRoleSessionDurationHours?: number;
  /** Callback to deactivate the current role */
  onDeactivateRole?: () => Promise<void>;
  /** Pending 2FA state for role activation */
  pendingRoleActivation2FA?: {
    tenantRoleId: string;
    codeId: string;
    destination: string;
    expiresIn: number;
    attemptsRemaining: number;
  } | null;
  /** Callback to verify 2FA code for role activation */
  onVerifyRoleActivation2FA?: (code: string) => Promise<void>;
  /** Callback to cancel pending 2FA */
  onCancelRoleActivation2FA?: () => void;
  /** Info about an expired role session (triggers expiry modal) */
  expiredRoleInfo?: ExpiredRoleInfo | null;
  /** Callback when user dismisses the expired role session modal */
  onDismissRoleSessionExpired?: () => Promise<void>;
  /** Callback when user wants to extend the expired role session */
  onExtendRoleSession?: () => Promise<void>;
  onLogout?: () => void;
  onProfileClick?: () => void;
  onSettingsClick?: () => void;
  onBillingClick?: () => void;
  showFooter?: boolean;
  customNavItems?: NavItem[];
}

const defaultNavItems: NavItem[] = [
  {
    label: "Dashboard",
    href: "/app/dashboard",
    icon: <HomeIcon className="h-5 w-5" />,
  },
  {
    label: "IAM",
    icon: <KeyIcon className="h-5 w-5" />,
    requiredPermission: "iam:GetUser",
    children: [
      { label: "Users", href: "/app/users", icon: <UsersIcon className="h-4 w-4" /> },
      { label: "Service Accounts", href: "/app/service-accounts", icon: <UserCircleIcon className="h-4 w-4" /> },
      { label: "Groups", href: "/app/groups", icon: <UserGroupIcon className="h-4 w-4" /> },
      { label: "Roles", href: "/app/roles", icon: <ShieldCheckIcon className="h-4 w-4" /> },
      { label: "Audit Log", href: "/app/audit-logs", icon: <ClipboardDocumentListIcon className="h-4 w-4" /> },
    ],
  },
  {
    label: "Applications",
    icon: <RectangleStackIcon className="h-5 w-5" />,
    requiredPermission: "applications:application:read",
    children: [
      { label: "Applications", href: "/app/applications", icon: <CubeIcon className="h-4 w-4" /> },
      { label: "App Jobs", href: "/app/app-jobs", icon: <ClockIcon className="h-4 w-4" /> },
      { label: "Background Jobs", href: "/app/background-jobs", icon: <QueueListIcon className="h-4 w-4" /> },
    ],
  },
  {
    label: "Resources",
    icon: <CubeIcon className="h-5 w-5" />,
    requiredPermission: "resources:volume:read",
    children: [
      { label: "Volumes", href: "/app/volumes", icon: <FolderIcon className="h-4 w-4" /> },
      { label: "Database Connections", href: "/app/database-connections", icon: <ServerStackIcon className="h-4 w-4" /> },
      { label: "Document Databases", href: "/app/document-databases", icon: <DocumentIcon className="h-4 w-4" /> },
      { label: "Managed SQL DBs", href: "/app/managed-sql-databases", icon: <CircleStackIcon className="h-4 w-4" /> },
      { label: "Secrets", href: "/app/secrets", icon: <LockClosedIcon className="h-4 w-4" /> },
    ],
  },
  {
    label: "Data Agents",
    icon: <CpuChipIcon className="h-5 w-5" />,
    requiredPermission: "elt:DescribeDataAgent",
    children: [
      { label: "Agents", href: "/app/data-agents", icon: <CpuChipIcon className="h-4 w-4" /> },
      { label: "ELT Jobs", href: "/app/elt-jobs", icon: <PlayCircleIcon className="h-4 w-4" /> },
      { label: "Job Executions", href: "/app/job-executions", icon: <ClockIcon className="h-4 w-4" /> },
    ],
  },
  {
    label: "Marketplace",
    icon: <ShoppingBagIcon className="h-5 w-5" />,
    requiredPermission: "marketplace:DescribeListing",
    children: [
      { label: "Browse", href: "/app/marketplace", icon: <MagnifyingGlassIcon className="h-4 w-4" /> },
      { label: "Installed Apps", href: "/app/marketplace-installed", icon: <CheckCircleIcon className="h-4 w-4" /> },
      { label: "Partner Dashboard", href: "/app/partner-dashboard", icon: <ChartBarIcon className="h-4 w-4" /> },
    ],
  },
  {
    label: "Launcher",
    href: "/app/launcher",
    icon: <RocketLaunchIcon className="h-5 w-5" />,
  },
  {
    label: "Support",
    href: "/app/support",
    icon: <LifebuoyIcon className="h-5 w-5" />,
    requiredPermission: "support:DescribeTicket",
  },
];

export const DashboardLayout = ({
  children,
  tenantCode,
  tenantName,
  tenantLogoUrl,
  userName,
  userEmail,
  activeRole,
  isRoleSessionActive = false,
  availableRoles = [],
  onActivateRole,
  defaultRoleSessionDurationHours,
  onDeactivateRole,
  pendingRoleActivation2FA,
  onVerifyRoleActivation2FA,
  onCancelRoleActivation2FA,
  expiredRoleInfo,
  onDismissRoleSessionExpired,
  onExtendRoleSession,
  onLogout,
  onProfileClick,
  onSettingsClick,
  onBillingClick,
  showFooter = true,
  customNavItems,
}: DashboardLayoutProps) => {
  const navItems = customNavItems || defaultNavItems;
  const location = useLocation();
  const breadcrumbItems = useBreadcrumbs();
  const pageTitle = breadcrumbItems[breadcrumbItems.length - 1]?.label;
  useDocumentTitle(pageTitle);
  const isDashboard = location.pathname === '/app/dashboard';

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      {/* Header - full width */}
      <Header
        tenantName={tenantName}
        tenantCode={tenantCode}
        tenantLogoUrl={tenantLogoUrl}
        userName={userName}
        userEmail={userEmail}
        activeRole={activeRole}
        isRoleSessionActive={isRoleSessionActive}
        availableRoles={availableRoles}
        onActivateRole={onActivateRole}
        defaultRoleSessionDurationHours={defaultRoleSessionDurationHours}
        onDeactivateRole={onDeactivateRole}
        pendingRoleActivation2FA={pendingRoleActivation2FA}
        onVerifyRoleActivation2FA={onVerifyRoleActivation2FA}
        onCancelRoleActivation2FA={onCancelRoleActivation2FA}
        onLogout={onLogout}
        onProfileClick={onProfileClick}
        onSettingsClick={onSettingsClick}
        onBillingClick={onBillingClick}
        logo={
          <div className="flex items-center gap-2">
            <img src={logoImage} alt="Ketrics" className="h-8 w-auto" />
          </div>
        }
        rightActions={<UnifiedInboxDropdown />}
      />

      {/* Content area with sidebar */}
      <div className="flex flex-1 min-w-0 overflow-hidden">
        {/* Sidebar filters items based on active role permissions */}
        <Sidebar activeRole={activeRole} customNavItems={navItems} />
        <main className="flex-1 min-w-0 overflow-y-auto">
          {!isDashboard && breadcrumbItems.length > 0 && (
            <div className="px-4 sm:px-6 lg:px-8 pt-4 pb-0">
              <Breadcrumb items={breadcrumbItems} />
            </div>
          )}
          <div className="py-6 px-4 sm:px-6 lg:px-8 overflow-x-auto">
            <PageTransition>{children}</PageTransition>
          </div>
        </main>
      </div>

      {/* Footer */}
      {showFooter && <Footer />}

      {/* Role Session Expired Modal */}
      {expiredRoleInfo && onDismissRoleSessionExpired && onExtendRoleSession && (
        <RoleSessionExpiredModal
          isOpen={!!expiredRoleInfo}
          roleCode={expiredRoleInfo.roleCode}
          roleName={expiredRoleInfo.roleName}
          onDismiss={onDismissRoleSessionExpired}
          onExtend={onExtendRoleSession}
        />
      )}
    </div>
  );
};

// Re-export types for backward compatibility
export type { NavItem as SidebarNavItem };
